<template>
  <tr
    class="hover:bg-gray-100 transition-colors duration-150 ease-in-out text-right child:py-4 first-child:pl-16 last-child:pr-3 text-sm cursor-pointer"
    v-bind="$attrs"
    :class="
      {
        'bg-gray-50 border-gray-100': depth <= 1,
        'bg-gray-200 border-gray-300': depth === 2,
        'bg-gray-300 border-gray-400': depth === 3,
        'bg-gray-400 border-gray-500': depth >= 4
      }
    "
    @click="showChildren = !showChildren"
  >
    <td class="text-left">
      <div
        class="flex items-start space-x-2 space-y-1"
        :style="{ paddingLeft: `${(depth * 20) + 8}px` }"
      >
        <button
          v-if="tradeItem.has_children"
          data-test="open-trade-item"
          type="button"
          class="btn-icon btn-icon-default"
          @click.stop="showChildren = !showChildren"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
            :class="[{'rotate-90': showChildren}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <p class="w-60 text-gray-700">
          {{ tradeItem.name }}
        </p>
      </div>
    </td>

    <td>
      {{ $tcc(childBudget) }}
    </td>

    <td class="pr-3" />

    <td class="bg-green-200/50">
      {{ $tcc(childClaim) }}
    </td>

    <td class="bg-green-200/50">
      {{ $tcc(childCertified) }}
    </td>

    <td
      class="bg-green-200/50"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
    >
      {{ hasVariance ? $tcc(variance) : '' }}
    </td>

    <td class="bg-green-200/50">
      {{ $tcc(childPrevious) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ $tcc(childPrevious + childCertified) }}
    </td>

    <td class="bg-blue-100/50">
      <span v-if="showPercentages">
        {{ childBudget > 0 ? `${$tn((childClaim / childBudget) * 100)}%` : '' }}
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ childBudget > 0 ? `${$tn((childCertified / childBudget) * 100)}%` : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ childBudget > 0 ? $tn((childPrevious / childBudget) * 100) + '%' : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ childBudget > 0 ? $tn(((childPrevious + childCertified) / childBudget) * 100) + '%' : '' }}
    </td>

    <td class="pr-3">
      {{ $tcc(Math.abs(childBudget - childPrevious - childCertified)) }}
    </td>
  </tr>

  <template v-if="relevantChildren.length > 0 && showChildren">
    <RatesTradeRowItem
      v-for="childItem in relevantChildren"
      :key="childItem.id"
      :trade="$props.trade"
      :claim="$props.claim"
      :trade-item="childItem"
      :package-trade-items="$props.packageTradeItems"
      :show-percentages="$props.showPercentages"
      :depth="depth ? depth + 1 : 1"
    />
  </template>
</template>

<script setup lang="ts">
import Claim from '@/models/Claim';
import PackageTradeItem from '@/models/PackageTradeItem';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useTradeItemsStore } from '@/store/tradeItems';
import { computed, ref } from 'vue';
import RatesTradeRowItem from './RatesTradeRowItem.vue';

const props = withDefaults(
  defineProps<{
    trade?: Trade;
    claim: Claim;
    tradeItem: TradeItem;
    packageTradeItems: PackageTradeItem[];
    showPercentages?: boolean;
    depth?: number;
  }>(),
  {
    depth: 0,
  },
);

defineOptions({
  inheritAttrs: false,
});

const tradeItemsStore = useTradeItemsStore();

const packageTradeItemIds = computed(() => {
  return props.packageTradeItems.map((packageTradeItem) => packageTradeItem.trade_item_id);
});

const hasPackageDescendant = (tradeItemId: Id): boolean => {
  if(packageTradeItemIds.value.some((id) => id === tradeItemId)) {
    return true;
  }

  return tradeItemsStore.models
    .where('parent_id', tradeItemId as number)
    .get()
    .some((child) => hasPackageDescendant(child.id));
};

const relevantChildren = computed(() => {
  return tradeItemsStore.models
    .where('parent_id', props.tradeItem.id)
    .orderBy('sort_order')
    .get()
    .filter((child) => hasPackageDescendant(child.id));
});

const descendantPackageTradeItems = computed((): PackageTradeItem[] => {
  const collect = (tradeItem: TradeItem): PackageTradeItem[] => {
    if(!tradeItem.has_children) {
      const packageTradeItem = props.packageTradeItems.find((item) => item.trade_item_id === tradeItem.id);

      return packageTradeItem ? [packageTradeItem] : [];
    }

    return tradeItemsStore.models
      .where('parent_id', tradeItem.id)
      .get()
      .flatMap((child) => collect(child));
  };

  return collect(props.tradeItem);
});

const showChildren = ref(relevantChildren.value.length > 0);

const childBudget = computed(() => {
  return descendantPackageTradeItems.value.reduce((total, packageTradeItem) => {
    return total + packageTradeItem.value;
  }, 0);
});

const childClaim = computed(() => {
  return descendantPackageTradeItems.value.reduce((total, packageTradeItem) => {
    const quantity = packageTradeItem.rates_costs.reduce((sum, cost) => sum + (cost.current_quantity || 0), 0);

    return total + (quantity * packageTradeItem.rate);
  }, 0);
});

const childCertified = computed(() => {
  return descendantPackageTradeItems.value.reduce((total, packageTradeItem) => {
    const quantity = packageTradeItem.rates_costs.reduce((sum, cost) => sum + (cost.current_certified || 0), 0);

    return total + (quantity * packageTradeItem.rate);
  }, 0);
});

const childPrevious = computed(() => {
  return descendantPackageTradeItems.value.reduce((total, packageTradeItem) => {
    return total + (packageTradeItem.previous(props.claim) * packageTradeItem.rate);
  }, 0);
});

const variance = computed(() => {
  return childClaim.value - childCertified.value;
});

const hasVariance = computed(() => {
  return true;
});
</script>

<style scoped></style>
